import React from 'react';
import { Card, Callout } from '../ui';
import ParcelForm from './ParcelForm';
import { Parcel } from './utils/calculationUtils';
import { UnitSystem } from './UnitSystem';

interface ParcelListProps {
  parcels: Parcel[];
  unitSystem: UnitSystem;
  onAddParcel: () => void;
  onRemoveParcel: (id: string) => void;
  onUpdateParcel: (id: string, field: string, value: number) => void;
}

const ParcelList: React.FC<ParcelListProps> = ({
  parcels,
  unitSystem,
  onAddParcel,
  onRemoveParcel,
  onUpdateParcel,
}) => {
  const weightUnit = unitSystem === 'metric' ? 'kg' : 'lb';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      {parcels.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {parcels.map((parcel, index) => (
            <Card
              key={parcel.id}
              pad
              style={{
                border: '1px solid var(--line)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 16, marginRight: 8 }}>📦</span>
                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-2)' }}>
                  Parcel {index + 1}
                </span>
                <div style={{ flex: 1 }} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 12,
                    background: 'var(--info-bg)',
                    color: 'var(--info)',
                    border: '1px solid var(--info)',
                  }}
                >
                  {Math.max(parcel.physicalWeight || 0, parcel.dimensionalWeight || 0).toFixed(2)} {weightUnit}
                </span>
              </div>
              <ParcelForm
                id={parcel.id}
                quantity={parcel.quantity}
                weight={parcel.weight}
                length={parcel.length}
                width={parcel.width}
                height={parcel.height}
                physicalWeight={parcel.physicalWeight}
                dimensionalWeight={parcel.dimensionalWeight}
                unitSystem={unitSystem}
                onUpdate={onUpdateParcel}
                onRemove={onRemoveParcel}
              />
            </Card>
          ))}
        </div>
      ) : (
        <Callout tone="info">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Add your first parcel to get started</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Include dimensions and weight for accurate shipping quotes.
            </div>
          </div>
        </Callout>
      )}

      {/* Add Parcel Button */}
      <button
        type="button"
        onClick={onAddParcel}
        style={{
          padding: '14px 20px',
          border: '2px dashed var(--line)',
          borderRadius: 8,
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--ink-3)',
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: '100%',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--brand-blue)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--brand-blue)';
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(43,85,255,0.04)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--line)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-3)';
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        <span style={{ fontSize: 18 }}>+</span>
        {parcels.length === 0 ? 'Add Your First Parcel' : 'Add Another Parcel'}
      </button>
    </div>
  );
};

export default ParcelList;
